/* Copyright (c) 2020-present CloudIO Inc */

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  createGenerateClassName,
  jssPreset,
  StylesProvider,
} from '@mui/styles';
import { create } from 'jss';
import rtl from 'jss-rtl';
import React, { useEffect, useMemo } from 'react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTheme } from 'theme/Theme';
import { useIsRTL } from '../utils/state';

const generateClassName = createGenerateClassName({
  productionPrefix: 'io-',
});

const jss = create({
  plugins: [...jssPreset().plugins, rtl()],
  insertionPoint: document.head,
});

export default function ThemeWrapper({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const theme = useTheme();
  const isRTL = useIsRTL();

  const cache = useMemo(() => {
    const _cache = createCache({
      key: 'io',
      prepend: true,
      stylisPlugins: isRTL ? [prefixer, rtlPlugin] : [prefixer],
    });
    _cache.compat = true;
    return _cache;
  }, [isRTL]);

  useEffect(() => {
    const dir = isRTL ? 'rtl' : 'ltr';
    document.dir = dir;
    document.body.dir = dir;
  }, [isRTL]);

  return (
    <StylesProvider jss={jss} generateClassName={generateClassName}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </StylesProvider>
  );
}
